import { SeparateOption } from 'player'
import { useCallback, useContext, useMemo } from 'react'
import { config } from '../../../config'
import { Context } from '../../../context'
import { definitions } from '../../../data/operations-defs'
import { Moises as MoisesNew } from '../../../lib/cli'
import { trigger } from '../../../lib/events'
import { Summary } from '../../../types'

interface UseChangeOperationsParams {
  summary: Summary
  trackId: string
}

interface SelectedSeparationType {
  id?: string
  params?: {
    type?: string
  }
  traits?: {
    stems?: number
    title?: string
    description?: string
    premiumOnly?: boolean
  }
}

interface SeparationOption {
  id: string
  params: {
    type: string
  }
  traits: {
    stems: number
    title: string
    description: string
    premiumOnly: boolean
  }
}

interface SeparationOptions {
  [key: string]: SeparationOption
}

export interface UseChangeOperations {
  options: SeparationOptions
  currentSeparationType: string
  selectedSeparation?: SelectedSeparationType
  handlePlayerSelect(option: Pick<SeparateOption, 'type'>): void
}

const MoisesCLI = MoisesNew({ apiEndpoint: config.api.endpoint })

export const useChangeOperations = ({
  summary,
  trackId
}: UseChangeOperationsParams): UseChangeOperations => {
  const { userToken } = useContext(Context)

  const currentSeparationType = useMemo(
    () => summary?.separate?.legacy?.type || 'custom',
    [summary]
  )

  const options = useMemo(
    () => ({
      'SEPARATE_vocals-drums-bass-other': {
        id: 'SEPARATE_B',
        params: {
          type: 'vocals-drums-bass-other'
        },
        traits: {
          stems: 4,
          title: 'separation.options.4tracks',
          description: 'separation.options.4tracks.title',
          premiumOnly: false
        }
      },
      'SEPARATE_vocals-accompaniment': {
        id: 'SEPARATE_B',
        params: {
          type: 'vocals-accompaniment'
        },
        traits: {
          stems: 2,
          title: 'separation.options.2tracks',
          description: 'separation.options.2tracks.title',
          premiumOnly: false
        }
      }
    }),
    []
  )

  const selectedSeparation = useMemo(() => {
    return Object.values(options).find(
      (option) => option.params.type === currentSeparationType
    )
  }, [currentSeparationType, options])

  const addOperation = useCallback(
    async (selectedOperation: string): Promise<string | undefined> => {
      MoisesCLI.auth(userToken)

      const definition = definitions[`SEPARATE_${selectedOperation}`]

      if (!definition) return undefined

      const result = await MoisesCLI.addOperationsToTask({
        trackId,
        operations: [
          {
            name: definition.id,
            params: definition.params
          }
        ]
      })

      await new Promise((resolve) => setTimeout(resolve, 500))

      trigger('revalidate:tasks')

      const [opId] = result.addOperationsToTrack
      return opId
    },
    [userToken, trackId]
  )

  const onConfirmAddOperation = useCallback(
    async (selectedOperation: string) => {
      await addOperation(selectedOperation)
    },
    [addOperation]
  )

  const handleSelectOperation = useCallback<
    UseChangeOperations['handlePlayerSelect']
  >(
    (option) => {
      const selectedOperation = option.type

      trigger('player:event-dispatch', {
        event: 'upgradability',
        value: 'change'
      })

      onConfirmAddOperation(selectedOperation)
    },
    [onConfirmAddOperation]
  )

  return {
    options,
    selectedSeparation,
    currentSeparationType,
    handlePlayerSelect: handleSelectOperation
  }
}
